/* eslint-disable no-unused-vars */
import React, { useState, useEffect } from 'react';
import { Grid, TextField, Typography, Box, Alert, Button } from '@mui/material';
import * as XLSX from 'xlsx';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import DownloadIcon from '@mui/icons-material/Download';
import config from '../config';

const ExcelUpload = ({ onValidationChange, onUserCountChange, onFileChange, existingFileName = null, existingFileSize = null, existingSIUserCount = null, requestId = null, numberOfDDI = null, requestType = 'webex' }) => {
    const [numberOfSIUsers, setNumberOfSIUsers] = useState('');
    const [excelError, setExcelError] = useState(null);
    const [validationSuccess, setValidationSuccess] = useState(false);
    const [uniqueNameCount, setUniqueNameCount] = useState(null);
    const [uniqueNumberCount, setUniqueNumberCount] = useState(null);
    const [currentFileName, setCurrentFileName] = useState(existingFileName);
    const [isDownloading, setIsDownloading] = useState(false);
    const [hasExistingFile, setHasExistingFile] = useState(false);
    const [duplicateNames, setDuplicateNames] = useState([]);
    const [duplicateNumbers, setDuplicateNumbers] = useState([]);

    // Initialize numberOfSIUsers from existing data
    useEffect(() => {
        if (existingSIUserCount !== null && existingSIUserCount !== undefined) {
            setNumberOfSIUsers(existingSIUserCount.toString());
            if (onUserCountChange) {
                onUserCountChange(existingSIUserCount);
            }
            // If we have existing file, mark as valid
            if (existingFileName) {
                setHasExistingFile(true);
                setValidationSuccess(true);
                if (onValidationChange) onValidationChange(true);
            }
        }
    }, [existingSIUserCount, existingFileName]);

    const handleDownloadExcel = async () => {
        if (!requestId || !existingFileName) {
            console.error('Cannot download: missing request ID or file name');
            return;
        }

        setIsDownloading(true);
        try {
            // Determine the correct API endpoint based on request type
            const endpoint = requestType === 'ipt'
                ? `${config.apiUrl}/api/ipt-decommissioning/${requestId}/download-excel`
                : `${config.apiUrl}/api/webex-decommissioning/${requestId}/download-excel`;

            const response = await fetch(
                endpoint,
                {
                    method: 'GET',
                    headers: {
                        'Authorization': `Bearer ${localStorage.getItem('token')}`
                    }
                }
            );

            if (!response.ok) {
                throw new Error('Failed to download file');
            }

            // Get the blob from response
            const blob = await response.blob();

            // Create a temporary URL for the blob
            const url = window.URL.createObjectURL(blob);

            // Create a temporary anchor element and trigger download
            const link = document.createElement('a');
            link.href = url;
            link.download = existingFileName;
            document.body.appendChild(link);
            link.click();

            // Cleanup
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
        } catch (error) {
            console.error('Error downloading Excel file:', error);
            setExcelError('Failed to download file. Please try again.');
        } finally {
            setIsDownloading(false);
        }
    };

    const handleExcelUpload = (event) => {
        const file = event.target.files[0];
        if (onFileChange) {
            onFileChange(file);
        }

        if (!file) {
            setExcelError('Please upload a valid Excel file.');
            setValidationSuccess(false);
            if (onValidationChange) onValidationChange(false);
            return;
        }

        // Update current file name when new file is uploaded
        setCurrentFileName(file.name);
        // Clear the existing file flag since we're uploading a new one
        setHasExistingFile(false);

        const reader = new FileReader();
        reader.onload = (e) => {
            try {
                const data = e.target.result;
                const workbook = XLSX.read(data, { type: 'binary' });
                const firstSheetName = workbook.SheetNames[0];
                const worksheet = workbook.Sheets[firstSheetName];
                const jsonData = XLSX.utils.sheet_to_json(worksheet);
                const uniqueNames = new Set();
                const uniqueNumbers = new Set();
                const nameOccurrences = {};
                const numberOccurrences = {};

                jsonData.forEach((row, index) => {
                    // Check for "Name" column (case-insensitive)
                    const nameKey = Object.keys(row).find(key =>
                        key.toLowerCase().includes('name')
                    );
                    if (nameKey && row[nameKey]) {
                        const nameValue = row[nameKey].toString().trim();
                        uniqueNames.add(nameValue);

                        // Track occurrences for duplicate detection
                        if (!nameOccurrences[nameValue]) {
                            nameOccurrences[nameValue] = [];
                        }
                        nameOccurrences[nameValue].push(index + 2); // +2 because Excel rows start at 1 and header is row 1
                    }

                    // Check for "Number" column (could be email or phone - case-insensitive)
                    const numberKey = Object.keys(row).find(key =>
                        key.toLowerCase().includes('number') ||
                          key.toLowerCase().includes('email') ||
                          key.toLowerCase().includes('phone')
                    );
                    if (numberKey && row[numberKey]) {
                        const numberValue = row[numberKey].toString().trim();
                        uniqueNumbers.add(numberValue);

                        // Track occurrences for duplicate detection
                        if (!numberOccurrences[numberValue]) {
                            numberOccurrences[numberValue] = [];
                        }
                        numberOccurrences[numberValue].push(index + 2);
                    }
                });

                // Find duplicates
                const foundDuplicateNames = Object.entries(nameOccurrences)
                    .filter(([_, rows]) => rows.length > 1)
                    .map(([value, rows]) => ({ value, rows }));

                const foundDuplicateNumbers = Object.entries(numberOccurrences)
                    .filter(([_, rows]) => rows.length > 1)
                    .map(([value, rows]) => ({ value, rows }));

                setUniqueNameCount(uniqueNames.size);
                setUniqueNumberCount(uniqueNumbers.size);
                setDuplicateNames(foundDuplicateNames);
                setDuplicateNumbers(foundDuplicateNumbers);
            } catch (error) {
                setExcelError('Error parsing the Excel file. Please ensure it has the correct format.');
                setValidationSuccess(false);
                if (onValidationChange) onValidationChange(false);
            }
        };

        reader.onerror = () => {
            setExcelError('Error reading the Excel file.');
            setValidationSuccess(false);
            if (onValidationChange) onValidationChange(false);
        };

        reader.readAsBinaryString(file);
    };

    // Validate whenever numberOfSIUsers, DDI, or counts change
    useEffect(() => {
        if (uniqueNameCount !== null && uniqueNumberCount !== null && numberOfSIUsers) {
            const siUserCount = parseInt(numberOfSIUsers);

            const shouldValidateDDI = numberOfDDI !== null && numberOfDDI !== undefined;

            let ddiCount = '';
            if (shouldValidateDDI) {
                ddiCount = numberOfDDI !== ''
                    ? parseInt(numberOfDDI)
                    : '';
            }

            if (isNaN(siUserCount)) {
                setExcelError('Please enter a valid number for SI Users.');
                setValidationSuccess(false);
                if (onValidationChange) onValidationChange(false);
                return;
            }

            // Check for duplicates first
            if (duplicateNames.length > 0 || duplicateNumbers.length > 0) {
                let duplicateMessage = '⚠️ Duplicate entries found in Excel file:\n\n';

                if (duplicateNames.length > 0) {
                    duplicateMessage += '📋 Duplicate Names:\n';
                    duplicateNames.slice(0, 5).forEach((dup, index) => {
                        duplicateMessage += `  ${index + 1}. "${dup.value}" appears in rows: ${dup.rows.join(', ')}\n`;
                    });
                    if (duplicateNames.length > 5) {
                        duplicateMessage += `  ... and ${duplicateNames.length - 5} more duplicate names\n`;
                    }
                    duplicateMessage += '\n';
                }

                if (duplicateNumbers.length > 0) {
                    duplicateMessage += '📧 Duplicate Numbers:\n';
                    duplicateNumbers.slice(0, 5).forEach((dup, index) => {
                        duplicateMessage += `  ${index + 1}. "${dup.value}" appears in rows: ${dup.rows.join(', ')}\n`;
                    });
                    if (duplicateNumbers.length > 5) {
                        duplicateMessage += `  ... and ${duplicateNumbers.length - 5} more duplicate numbers\n`;
                    }
                }

                duplicateMessage += '\n⚠️ Please remove all duplicate entries from your Excel file and upload again.';

                setExcelError(duplicateMessage);
                setValidationSuccess(false);
                if (onValidationChange) onValidationChange(false);
                if (onUserCountChange) onUserCountChange(0);
                return;
            }

            const isValid = shouldValidateDDI
                ? (uniqueNameCount === siUserCount && uniqueNumberCount === siUserCount && ddiCount === siUserCount)
                : (uniqueNameCount === siUserCount && uniqueNumberCount === siUserCount);

            if (isValid) {
                setExcelError(null);
                setValidationSuccess(true);
                if (onValidationChange) onValidationChange(true);
                if (onUserCountChange) onUserCountChange(siUserCount);
            } else {
                // Build error message based on what doesn't match
                let errorParts = [];

                // Only check DDI if in IPT mode
                if (shouldValidateDDI && ddiCount !== siUserCount) {
                    const displayDDI = (ddiCount === '' || isNaN(ddiCount)) ? '' : ddiCount;
                    errorParts.push(`DDI (${displayDDI})`);
                }
                if (uniqueNameCount !== siUserCount) {
                    errorParts.push(`Unique Name (${uniqueNameCount})`);
                }
                if (uniqueNumberCount !== siUserCount) {
                    errorParts.push(`Unique Number (${uniqueNumberCount})`);
                }

                // Build error message based on mode
                const errorMessage = shouldValidateDDI
                    ? `Validation Failed: ${errorParts.join(', ')} must match SI Users (${siUserCount}). All values should be equal: DDI = SI Users = Unique Name = Unique Number.`
                    : `Validation Failed: ${errorParts.join(', ')} must match SI Users (${siUserCount}).`;

                setExcelError(errorMessage);
                setValidationSuccess(false);
                if (onValidationChange) onValidationChange(false);
                if (onUserCountChange) onUserCountChange(0);
            }
        } else if (uniqueNameCount !== null || uniqueNumberCount !== null) {
            if (!numberOfSIUsers) {
                setExcelError('Please enter the Number of SI Users to validate.');
                setValidationSuccess(false);
                if (onValidationChange) onValidationChange(false);
            }
        }
    }, [numberOfSIUsers, uniqueNameCount, uniqueNumberCount, numberOfDDI, duplicateNames, duplicateNumbers, onValidationChange, onUserCountChange]);

    return (
          <Grid container spacing={2} sx={{ mt: 2 }}>
              <Grid item xs={6}>
                  <TextField
                      label="Number of SI Users"
                      fullWidth
                      type="number"
                      value={numberOfSIUsers}
                      onChange={(e) => {
                          const value = e.target.value;
                          if (value === '' || parseFloat(value) >= 0) {
                              setNumberOfSIUsers(value);
                          }
                      }}
                      placeholder="Enter number of SI users"
                      inputProps={{ min: 0 }}
                  />
              </Grid>

              <Grid item xs={6}>
                  <TextField
                      label="Excel Upload"
                      type="file"
                      fullWidth
                      onChange={handleExcelUpload}
                      InputLabelProps={{ shrink: true }}
                      inputProps={{ accept: '.xlsx, .xls' }}
                  />
              </Grid>

              {/* Show existing/current file information ONLY when editing a saved draft */}
              {currentFileName && existingFileName && (
                  <Grid item xs={12}>
                      <Alert
                          severity="info"
                          action={
                              currentFileName === existingFileName && requestId && (
                                  <Button
                                      color="inherit"
                                      size="small"
                                      startIcon={<DownloadIcon />}
                                      onClick={handleDownloadExcel}
                                      disabled={isDownloading}
                                  >
                                      {isDownloading ? 'Downloading...' : 'Download'}
                                  </Button>
                              )
                          }
                      >
                          <Typography variant="body2">
                              <strong>Current File:</strong> {currentFileName}
                              {existingFileSize && ` (${(existingFileSize / 1024).toFixed(2)} KB)`}
                          </Typography>
                          {currentFileName === existingFileName && (
                              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                                  This is the previously uploaded file. Upload a new file to replace it.
                              </Typography>
                          )}
                      </Alert>
                  </Grid>
              )}

              {/* Validation Messages */}
              <Grid item xs={12}>
                  {validationSuccess && hasExistingFile && (
                      <Alert severity="success" icon={<CheckCircleIcon />}>
                          <Typography variant="body2">
                              <strong>Previously Uploaded File Loaded!</strong> This draft contains an Excel file with {numberOfSIUsers} SI Users.
                          </Typography>
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                              Upload a new Excel file to replace it and re-validate, or proceed with the existing file.
                          </Typography>
                      </Alert>
                  )}
                  {validationSuccess && !hasExistingFile && uniqueNameCount !== null && uniqueNumberCount !== null && (
                      <Alert severity="success" icon={<CheckCircleIcon />}>
                          <Typography variant="body2">
                              <strong>Validation Successful!</strong> {numberOfDDI !== null && numberOfDDI !== undefined
                                  ? `DDI (${numberOfDDI}), SI Users (${numberOfSIUsers}), Unique Name (${uniqueNameCount}), and Unique Number (${uniqueNumberCount}) all match.`
                                  : `Unique Name (${uniqueNameCount}) and Unique Number (${uniqueNumberCount}) match SI Users (${numberOfSIUsers}).`
                              }
                          </Typography>
                      </Alert>
                  )}
                  {excelError && (
                      <Alert severity="error">
                          <Typography variant="body2" sx={{ whiteSpace: 'pre-line' }}>{excelError}</Typography>
                      </Alert>
                  )}
                  {uniqueNameCount !== null && uniqueNumberCount !== null && !validationSuccess && !excelError && (
                      <Box sx={{ mt: 1 }}>
                          <Typography variant="caption" color="text.secondary">
                              Unique Emails: {uniqueNameCount} | Unique Users: {uniqueNumberCount}
                          </Typography>
                      </Box>
                  )}
              </Grid>
          </Grid>
    );
};

export default ExcelUpload;
